<script lang="ts" setup>
import type { PersonFields } from '~/components/Person/CreateOrEdit/Inputs.vue'
import type { AccountRole, RouterOutput, UpdateOrCreateAccount } from '~/types'
import { defu } from 'defu'

const props = defineProps<{
  payload: UpdateOrCreateAccount
  roles: AccountRole[]
  options?: Partial<AccountCreateOrUpdateOptions>
  predispositions?: RouterOutput['predispositions']['findManyPredisposition']['data']
  disabled?: boolean
  isLoading?: boolean
}>()

const emit = defineEmits<{ (e: 'save', payload: UpdateOrCreateAccount): void, (e: 'cancel'): void }>()

export type AccountCreateOrUpdateOptions = {
  showAccountDetails: boolean
  showPersonDetails: PersonFields
  showNameDetails: boolean
  allowRoleSelect: boolean
  userRegistrationForm?: { byEmail?: boolean, byMobilePhone?: boolean }
}

const defaultOptions: AccountCreateOrUpdateOptions = {
  showAccountDetails: true,
  showNameDetails: true,
  showPersonDetails: false,
  allowRoleSelect: false,
  userRegistrationForm: undefined,
}
const options = defu(props.options, defaultOptions)

const { canUse, isCityAccount } = useAuthorization()
const { cloned } = useCloned(props.payload)

const { isDirty } = useDirtyForm(props.payload.data, computed(() => cloned.value.data))
defineExpose({ isDirty })

const selectedRole = ref<AccountRole>(cloned.value.data.role)
const showRoleSwitchWarning = ref(false)
const hasRoleChanged = computed(() => props.payload.mode === 'update' && props.payload.data.role !== cloned.value.data.role)

const submitHandler = () => {
  // Check if the role has not changed or the warning is already open
  if (showRoleSwitchWarning.value || !hasRoleChanged.value) {
    showRoleSwitchWarning.value = false
    return emit('save', cloned.value)
  }
  showRoleSwitchWarning.value = true
}
const isEmailRequired = computed(() => {
  if (isCityAccount.value) {
    // For city employees, email is only required when creating accounts other than citizens accounts
    return selectedRole.value !== 'person'
  }
  // For citizens, email is required if mobilePhone is not set
  return !cloned.value.data.mobilePhone
})
const isMobilePhoneRequired = computed(() => {
  if (isCityAccount.value) {
    // For city employees, mobilePhone is not required
    return false
  }
  // For citizens, mobilePhone required if email is not set
  return !cloned.value.data.email
})
</script>

<template>
  <FormKitForm
    v-model="cloned.data"
    :mode="payload.mode"
    :disabled="disabled"
    :is-loading="isLoading"
    :custom-save-btn="options?.userRegistrationForm ? 'register.asUser' : undefined"
    :full-width-action-buttons="options?.userRegistrationForm ? true : false"
    @submit="submitHandler"
    @cancel="$emit('cancel')"
  >
    <AccountRegistrationUserInputs
      v-if="options?.userRegistrationForm"
      :options="options.userRegistrationForm"
    />
    <template v-else>
      <n-alert
        v-if="hasRoleChanged"
        :title="$t('account.roleWarning.title')"
        type="warning"
        class="mb-4"
      >
        {{ $t('account.roleWarning.text', { role: $t(`roles.${cloned.data.role}`) }) }}
      </n-alert>
      <FormKit name="person" type="group">
        <PersonCreateOrEditInputs
          :predispositions="predispositions"
          :person-fields="options.showPersonDetails"
          :show-name="options.showNameDetails"
          :selected-role="selectedRole"
          :has-reporting-address="!!payload.data.person?.reportingAddress"
        />
      </FormKit>
      <template v-if="options.showAccountDetails">
        <FormKit
          v-if="payload.mode === 'create' || canUse('account.update.identifier')"
          name="email"
          type="email"
          :label="$t('account.email.label')"
          :placeholder="$t('account.email.placeholder')"
          :validation="[isEmailRequired ? ['required'] : [''], ['email']]"
        />
        <div class="md:grid grid-flow-col grid-rows-1 md:gap-4">
          <FormKit
            v-if="payload.mode === 'create' || canUse('account.update.identifier')"
            name="mobilePhone"
            type="tel"
            maxlength="20"
            :label="$t('person.mobilePhone.label')"
            :placeholder="$t('person.mobilePhone.placeholder')"
            :validation="[isMobilePhoneRequired ? ['required'] : [''], ['mobilePhone'], ['matches', /^\+?[0-9 \-()]+$/]]"
            :validation-messages="{ mobilePhone: $t('notifications.error.invalidMobilePhone') }"
          />
          <FormKit
            name="landLinePhone"
            type="tel"
            :label="$t('person.landLinePhone.label')"
            :placeholder="$t('person.landLinePhone.placeholder')"
            :validation="[['matches', /^\+?[0-9 ]+$/]]"
          />
        </div>
        <FormKitMultiSelect
          v-if="canUse('account.role.update') && options.allowRoleSelect && roles.includes(cloned.data.role)"
          v-model="selectedRole"
          name="role"
          :disabled="disabled"
          :label="$t('account.role.label')"
          :placeholder="$t('account.role.placeholder')"
          validation="required"
          :is-multi-select-allowed="false"
          :multi-select-options="generateTranslatedSelectOptions('roles', roles)"
        />
      </template>
    </template>
  </FormKitForm>
  <TheConfirmPopup
    v-if="showRoleSwitchWarning"
    :is-loading="isLoading"
    :show="Boolean(showRoleSwitchWarning)"
    :title="$t('account.roleWarning.title')"
    :text="$t('account.roleWarning.text', { role: $t(`roles.${cloned.data.role}`) })"
    @close="showRoleSwitchWarning = false"
    @confirm="submitHandler"
  />
</template>
